import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { AuthLayout } from "@/features/auth/components/AuthLayout";
import { useAppForm } from "@/lib/form/form-hook";
import { createRegisterSchema } from "@/lib/form/schemas/auth";
import { useRegister } from "@/features/auth/hooks/useRegister";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { CheckCircle } from "lucide-react";

export const Route = createFileRoute("/register")({
  component: RegisterPage,
});

function RegisterPage() {
  const { t } = useTranslation();
  const registerMutation = useRegister();
  const schema = createRegisterSchema(t);
  const [success, setSuccess] = useState(false);

  const form = useAppForm({
    defaultValues: {
      name: "",
      email: "",
      password: "",
    },
    validators: {
      onBlur: schema,
    },
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value }) => {
      try {
        await registerMutation.mutateAsync(value);
        setSuccess(true);
      } catch {
        // Errors handled below via registerMutation.error
      }
    },
  });

  // Success state
  if (success) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
            <CheckCircle size={32} className="text-success" />
          </div>
          <h1 className="text-xl font-bold text-heading">{t("auth.register.success")}</h1>
          <Link
            to="/login"
            className="mt-2 text-sm font-medium text-indigo hover:text-indigo-bright transition-colors"
          >
            {t("auth.login.submit")}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {/* Header */}
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-bold text-heading">{t("auth.register.title")}</h1>
        <p className="mt-1 text-sm text-muted">{t("auth.register.subtitle")}</p>
      </div>

      {/* API error banner */}
      <ApiErrorBanner
        error={registerMutation.error}
        fieldLabels={{
          name: t("auth.register.name"),
          email: t("auth.register.email"),
          password: t("auth.register.password"),
        }}
        className="mb-4"
      />

      {/* Form */}
      <form.AppForm>
        <form.Form className="flex flex-col gap-4" noValidate>
          <form.AppField
            name="name"
            children={(field) => (
              <field.TextField
                label={t("auth.register.name")}
                placeholder={t("auth.register.namePlaceholder")}
                autoComplete="name"
              />
            )}
          />

          <form.AppField
            name="email"
            children={(field) => (
              <field.TextField
                label={t("auth.register.email")}
                type="email"
                placeholder={t("auth.register.emailPlaceholder")}
                autoComplete="email"
              />
            )}
          />

          <form.AppField
            name="password"
            children={(field) => (
              <field.PasswordField
                label={t("auth.register.password")}
                placeholder={t("auth.register.passwordPlaceholder")}
                autoComplete="new-password"
              />
            )}
          />

          {/* Submit */}
          <form.SubmitButton
            label={t("auth.register.submit")}
            loadingLabel={t("auth.register.submitting")}
          />
        </form.Form>
      </form.AppForm>

      {/* Login link */}
      <p className="mt-6 text-center text-sm text-muted">
        {t("auth.register.hasAccount")}{" "}
        <Link
          to="/login"
          className="font-medium text-indigo hover:text-indigo-bright transition-colors"
        >
          {t("auth.register.login")}
        </Link>
      </p>
    </AuthLayout>
  );
}
